import React, { useEffect, useState } from 'react';
import {
  Flex,
  Spinner,
  HStack,
  Input,
  Button,
  Text,
  Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useDoorData } from '../context/DoorContext';

import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from '../DataTable';

function LogPage() {
  const {
    user,
    allLog,
    allLogs,
    getDoorStatusBadge,
    loading,
    loadDataFromEmaitinimas,
  } = useDoorData();
  const navigate = useNavigate();

  type AccessLog = {
    id: string;
    name: string;
    surname: string;
    pname: string;
    psurname: string;
    card_no: string;
    groupname: string;
    door_id: string | number;
    direction: string;
    date: Date;
    action: string;
  };

  const columnHelper = createColumnHelper<AccessLog>();

  // siauri stulpeliai
  const columns = [
  columnHelper.accessor('id', {
    header: () => <Text fontSize="xs" w="45px" textAlign="center">ID</Text>,
    cell: info => <Text fontSize="xs" w="45px" textAlign="center">{info.getValue()}</Text>
  }),

  columnHelper.accessor('name', {
    header: () => <Text fontSize="xs" w="100px">Vardas</Text>,
    cell: info => <Text fontSize="xs" w="100px" noOfLines={1}>{info.getValue()}</Text>
  }),

  columnHelper.accessor('surname', {
    header: () => <Text fontSize="xs" w="110px">Pavardė</Text>,
    cell: info => <Text fontSize="xs" w="110px" noOfLines={1}>{info.getValue()}</Text>
  }),

  columnHelper.accessor('card_no', {
    header: () => <Text fontSize="xs" w="90px">Kort. Nr.</Text>,
    cell: info => <Text fontSize="xs" w="90px" noOfLines={1}>{info.getValue()}</Text>
  }),

  // 👇 PALIEKAM DURIS
  columnHelper.accessor('door_id', {
    header: () => <Text fontSize="xs" w="70px">Durys</Text>,
    cell: info => <Text fontSize="xs" w="70px" noOfLines={1}>{info.getValue()}</Text>
  }),

  columnHelper.accessor('date', {
    header: () => <Text fontSize="xs" w="140px">Data</Text>,
    cell: info => <Text fontSize="xs" w="140px" noOfLines={1}>{info.getValue()}</Text>
  }),

  columnHelper.accessor('action', {
    header: () => <Text fontSize="xs" w="90px">Veiksmas</Text>,
    cell: info => <Text fontSize="xs" w="90px" noOfLines={1}>{info.getValue()}</Text>
  })
];

  // ---- DATOS FILTRAI (siunčiami į serverį) ----
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const [fromDate, setFromDate] = useState<string>(weekAgo);
  const [toDate, setToDate] = useState<string>(today);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // pirmas užkrovimas – paskutinė savaitė
    allLogs(weekAgo, today);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFilter = () => {
    allLogs(fromDate || undefined, toDate || undefined);
  };

  const handleClearFilter = () => {
    setFromDate('');
    setToDate('');
    allLogs(); // vėl be filtrų
  };

  const applyQuickRange = (type: 'today' | 'week' | 'month') => {
    const now = new Date();
    const todayString = now.toISOString().split('T')[0];

    let from = todayString;

    if (type === 'week') {
      const weekAgoLocal = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      from = weekAgoLocal;
    }

    if (type === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      from = monthAgo.toISOString().split('T')[0];
    }

    setFromDate(from);
    setToDate(todayString);
    allLogs(from, todayString);
  };

  const data: any[] =
    allLog.map((row: any) => ({
      id: row.id.toString(),
      name: loadDataFromEmaitinimas ? row.name : row.pname,
      surname: loadDataFromEmaitinimas ? row.surname : row.psurname,
      card_no: row.card_no,
      groupname: row.groupname,
      door_id: row.door_id === 0 ? 'Visos' : row.door_id.toString(),
      direction:
        row.direction === 'OUTSIDE'
          ? 'Išorinės'
          : row.direction === 'INSIDE'
          ? 'Vidinės'
          : row.direction,
      date: new Date(row.date).toLocaleString('lt-LT', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      action: getDoorStatusBadge(row.action).text,
    })) || [];

  return (
    <>
      {loading ? (
        <Flex w={'90vw'} alignItems={'center'} justifyContent={'center'}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="green.500"
            size="xl"
          />
        </Flex>
      ) : (
        <Flex direction="column" mx="5vw" gap={4}>
          {/* STICKY FILTRŲ JUOSTA */}
         <Box
            position="sticky"
            top="0"
            zIndex={10}
            bg="gray.50"
            borderBottom="1px solid #ddd"
          >
            <Flex
              bg="gray.50"
              border="1px solid #ddd"
              borderRadius="md"
              p={4}
              align="center"
              justify="flex-start"
              gap={4}
              wrap="wrap"
              direction={{ base: 'column', md: 'row' }}
            >
              {/* DATOS */}
              <Flex gap={4} wrap="wrap" width={{ base: '100%', md: 'auto' }}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    Nuo
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    bg="white"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    width={{ base: '100%', md: 'auto' }}
                  />
                </HStack>

                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    Iki
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    bg="white"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    width={{ base: '100%', md: 'auto' }}
                  />
                </HStack>
              </Flex>

              {/* BUTTONS */}
              <Flex gap={2} wrap="wrap" width={{ base: '100%', md: 'auto' }}>
                <Button size="sm" colorScheme="green" onClick={handleFilter}>
                  Filtruoti
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.600"
                  _hover={{ bg: 'gray.100' }}
                  onClick={handleClearFilter}
                >
                  Išvalyti
                </Button>
              </Flex>

              {/* QUICK FILTERS */}
              <Flex gap={2} wrap="wrap" width={{ base: '100%', md: 'auto' }}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => applyQuickRange('today')}
                >
                  Šiandien
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => applyQuickRange('week')}
                >
                  Ši savaitė
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => applyQuickRange('month')}
                >
                  Šis mėnuo
                </Button>
              </Flex>
            </Flex>
          </Box>

          {/* LENTELĖ */}
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={data}
              name="Atsisiųsti istoriją"
            />
          </Box>
        </Flex>
      )}
    </>
  );
}

export default LogPage;
